import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 任务中心所有端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`，
// 否则页面会拿到 401 并显示后端原文 "Authentication required"（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type TaskCenterItemDto = ApiComponents['schemas']['TaskCenterItemDto'];
export type CreateTaskCenterItemDto =
  ApiComponents['schemas']['CreateTaskCenterItemDto'];
export type AssignTaskCenterItemDto =
  ApiComponents['schemas']['AssignTaskCenterItemDto'];
export type StartTaskCenterItemDto =
  ApiComponents['schemas']['StartTaskCenterItemDto'];
export type CompleteTaskCenterItemDto =
  ApiComponents['schemas']['CompleteTaskCenterItemDto'];
export type CancelTaskCenterItemDto =
  ApiComponents['schemas']['CancelTaskCenterItemDto'];
export type DeferTaskCenterItemDto =
  ApiComponents['schemas']['DeferTaskCenterItemDto'];
export type ReviveTaskCenterItemDto =
  ApiComponents['schemas']['ReviveTaskCenterItemDto'];

export type TaskCenterStatus = TaskCenterItemDto['status'];
export type TaskCenterPriority = TaskCenterItemDto['priority'];
export type TaskCenterSource = TaskCenterItemDto['source'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 403) {
    return { message: '当前角色没有任务中心访问权限', code: 'FORBIDDEN' };
  }
  if (status === 409) {
    return {
      message: '任务已被其他用户修改或状态已变更，请刷新后重试',
      code: 'CONFLICT',
    };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

export interface ListTaskCenterFilter {
  status?: TaskCenterStatus;
  priority?: TaskCenterPriority;
  source?: TaskCenterSource;
  assigneeId?: string;
  ownerUserId?: string;
  linkedCustomerId?: string;
  linkedOrderId?: string;
  dueAtFrom?: string;
  dueAtTo?: string;
}

export function useTaskCenter() {
  const [tasks, setTasks] = useState<TaskCenterItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchTasks = useCallback(async (params?: ListTaskCenterFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string> = {};
      if (params?.status) query.status = params.status;
      if (params?.priority) query.priority = params.priority;
      if (params?.source) query.source = params.source;
      if (params?.assigneeId) query.assigneeId = params.assigneeId;
      if (params?.ownerUserId) query.ownerUserId = params.ownerUserId;
      if (params?.linkedCustomerId)
        query.linkedCustomerId = params.linkedCustomerId;
      if (params?.linkedOrderId) query.linkedOrderId = params.linkedOrderId;
      if (params?.dueAtFrom) query.dueAtFrom = params.dueAtFrom;
      if (params?.dueAtTo) query.dueAtTo = params.dueAtTo;
      const {
        data,
        error: apiErr,
        response,
      } = await apiClient.GET('/api/v1/task-center', {
        params: { query },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setTasks([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const createTask = useCallback(
    async (
      dto: CreateTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center', { body: dto });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const assignTask = useCallback(
    async (
      id: string,
      dto: AssignTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center/{id}/assign', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const startTask = useCallback(
    async (
      id: string,
      dto: StartTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center/{id}/start', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const completeTask = useCallback(
    async (
      id: string,
      dto: CompleteTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center/{id}/complete', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const cancelTask = useCallback(
    async (
      id: string,
      dto: CancelTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center/{id}/cancel', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const deferTask = useCallback(
    async (
      id: string,
      dto: DeferTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center/{id}/defer', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const reviveTask = useCallback(
    async (
      id: string,
      dto: ReviveTaskCenterItemDto,
    ): Promise<TaskCenterItemDto | null> => {
      setError(null);
      try {
        const {
          data,
          error: apiErr,
          response,
        } = await apiClient.POST('/api/v1/task-center/{id}/revive', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  return {
    tasks,
    loading,
    error,
    fetchTasks,
    createTask,
    assignTask,
    startTask,
    completeTask,
    cancelTask,
    deferTask,
    reviveTask,
  };
}
